import { useCallback, useEffect, useRef } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import Point from '@arcgis/core/geometry/Point';
import Polygon from '@arcgis/core/geometry/Polygon';
import Graphic from '@arcgis/core/Graphic';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import SimpleFillSymbol from '@arcgis/core/symbols/SimpleFillSymbol';
import type { Geometry } from 'geojson';
import {
  getIncidentIcon,
  getIncidentIconSymbol,
} from '../../IncidentsTab/utils/incidentIconUtils';
import { IncidentPopupContent } from '../../IncidentsTab/IncidentPopupContent';
import type { IncidentListItemData } from '../../IncidentsTab/Incidents';

const LAYER_POLYGON_FILL = [30, 64, 175, 0.25] as [
  number,
  number,
  number,
  number,
];
const LAYER_POLYGON_OUTLINE_COLOR = [30, 58, 138, 1] as [
  number,
  number,
  number,
  number,
];

const INCIDENT_ICONS_LAYER_ID = 'incident-icons-layer';

function geoJsonToArcgisPolygon(geometry: Geometry): Polygon | null {
  if (geometry.type === 'Polygon') {
    return new Polygon({ rings: geometry.coordinates });
  }
  if (geometry.type === 'MultiPolygon') {
    return new Polygon({
      rings: geometry.coordinates.flatMap((poly) => poly),
    });
  }
  return null;
}

export const useIncidentLayerSync = ({
  mapView,
  visibleIncidents,
  hiddenIncidentIds,
}: {
  mapView: __esri.MapView | null;
  visibleIncidents: IncidentListItemData[];
  hiddenIncidentIds: string[];
}) => {
  const incidentIconsLayerRef = useRef<GraphicsLayer | null>(null);
  const popupRootRef = useRef<Root | null>(null);

  useEffect(() => {
    if (!mapView?.map) return;

    const existingLayer = mapView.map.findLayerById(
      INCIDENT_ICONS_LAYER_ID
    ) as GraphicsLayer | null;

    if (existingLayer) {
      incidentIconsLayerRef.current = existingLayer;
    } else {
      const layer = new GraphicsLayer({
        id: INCIDENT_ICONS_LAYER_ID,
        title: 'Incident Icons',
      });
      mapView.map.add(layer);
      incidentIconsLayerRef.current = layer;
    }

    return () => {
      if (popupRootRef.current) {
        popupRootRef.current.unmount();
        popupRootRef.current = null;
      }
    };
  }, [mapView]);

  useEffect(() => {
    const layer = incidentIconsLayerRef.current;
    if (!layer) return;

    layer.removeAll();

    const hiddenSet = new Set(hiddenIncidentIds);
    const incidentsToShow = visibleIncidents.filter(
      (incident) =>
        !hiddenSet.has(incident.id) &&
        incident.latLong &&
        getIncidentIcon(incident.incidentTypes) !== null
    );

    incidentsToShow.forEach((incident) => {
      const arcgisPolygon = incident.locationGeometry
        ? geoJsonToArcgisPolygon(incident.locationGeometry)
        : null;

      if (arcgisPolygon) {
        // Render polygon footprint
        layer.add(
          new Graphic({
            geometry: arcgisPolygon,
            symbol: new SimpleFillSymbol({
              color: LAYER_POLYGON_FILL,
              outline: {
                color: LAYER_POLYGON_OUTLINE_COLOR,
                width: 2,
              },
            }),
            attributes: {
              incidentId: incident.id,
              name: incident.name,
            },
          })
        );
      }

      // Always render the icon point marker (on top of the polygon if present)
      const symbol = getIncidentIconSymbol(incident);
      if (!symbol) return;

      layer.add(
        new Graphic({
          geometry: new Point({
            longitude: incident.latLong!.longitude,
            latitude: incident.latLong!.latitude,
          }),
          symbol,
          attributes: {
            incidentId: incident.id,
            name: incident.name,
          },
        })
      );
    });
  }, [hiddenIncidentIds, visibleIncidents]);

  const handleIncidentIconClick = useCallback(
    async (event: unknown) => {
      const layer = incidentIconsLayerRef.current;
      if (!layer || !mapView) return;

      const hitResult = await mapView.hitTest(event as MouseEvent, {
        include: [layer],
      });

      const clickedGraphicHit = hitResult.results.find(
        (result) =>
          result.type === 'graphic' &&
          result.graphic.layer?.id === INCIDENT_ICONS_LAYER_ID
      );

      if (!clickedGraphicHit || clickedGraphicHit.type !== 'graphic') return;

      const clickedGraphic = clickedGraphicHit.graphic;
      const incidentId = String(clickedGraphic.attributes?.incidentId || '');
      const incident = visibleIncidents.find((item) => item.id === incidentId);

      if (!incident || !incident.latLong) return;

      const zoomLocation = new Point({
        longitude: incident.latLong.longitude,
        latitude: incident.latLong.latitude,
      });

      const popupContent = document.createElement('div');
      const root = createRoot(popupContent);
      popupRootRef.current = root;
      root.render(<IncidentPopupContent incident={incident} />);

      const popupLocation = new Point({
        longitude: incident.latLong.longitude,
        latitude: incident.latLong.latitude,
      });

      await mapView.goTo(zoomLocation);

      await mapView.openPopup({
        title: incident.name,
        location: popupLocation,
        content: popupContent,
      });

      const popup = mapView.popup;
      if (!popup) return;

      const handle = popup.watch('visible', (visible) => {
        if (!visible) {
          root.unmount();
          if (popupRootRef.current === root) {
            popupRootRef.current = null;
          }
          handle.remove();
        }
      });
    },
    [mapView, visibleIncidents]
  );

  useEffect(() => {
    if (!mapView) return;

    const clickHandle = mapView.on('click', handleIncidentIconClick);

    return () => {
      clickHandle.remove();
    };
  }, [mapView, handleIncidentIconClick]);
};
